import { AfterViewInit, Directive, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';

/** Gira una pirámide para que la raíz quede a la izquierda y las ramas crezcan a la derecha. */
@Directive({
  selector: '[appOrganigramaHorizontal]',
  standalone: true,
})
export class OrganigramaHorizontalDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private observer?: ResizeObserver;

  ngAfterViewInit(): void {
    const giro = this.giro();
    if (!giro) {
      return;
    }
    this.observer = new ResizeObserver(() => this.ajustar());
    this.observer.observe(giro);
    this.ajustar();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private giro(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.org-h__giro');
  }

  private ajustar(): void {
    const giro = this.giro();
    if (!giro) {
      return;
    }
    const ancho = giro.offsetWidth;
    const alto = giro.offsetHeight;
    if (ancho === 0 || alto === 0) {
      return;
    }
    const slot = this.host.nativeElement;
    slot.style.width = `${alto}px`;
    slot.style.height = `${ancho}px`;
    giro.style.top = `${ancho}px`;
    slot.classList.add('org-h--listo');
    intentarAnimacionOrganigrama(
      () => this.ajustar(),
      () => nodosGirados(slot),
    );
  }
}

/**
 * El gráfico completo se gira -90°. Cada tarjeta se gira +90° adentro de un
 * casillero invertido, así el texto queda derecho y las líneas siguen alineadas.
 */
@Directive({
  selector: '[appOrganigramaNodo]',
  standalone: true,
})
export class OrganigramaNodoDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private observer?: ResizeObserver;

  ngAfterViewInit(): void {
    const nodo = this.host.nativeElement.querySelector('.organigrama-nodo') as HTMLElement | null;
    if (!nodo) {
      return;
    }
    const intentar = (): void => {
      if (nodo.dataset['girado'] === '1') {
        return;
      }
      const ancho = nodo.offsetWidth;
      const alto = nodo.offsetHeight;
      if (ancho < 8 || alto < 8) {
        return;
      }
      const slot = this.host.nativeElement;
      slot.style.width = `${alto}px`;
      slot.style.height = `${ancho}px`;
      nodo.style.boxSizing = 'border-box';
      nodo.style.width = `${ancho}px`;
      nodo.style.height = `${alto}px`;
      nodo.style.position = 'absolute';
      nodo.style.left = `${alto}px`;
      nodo.style.top = '0';
      nodo.style.transformOrigin = 'top left';
      nodo.style.transform = 'rotate(90deg)';
      nodo.dataset['girado'] = '1';
      this.observer?.disconnect();
    };
    this.observer = new ResizeObserver(() => intentar());
    this.observer.observe(nodo);
    intentar();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}

const DURACION_MS = 1000;

interface Punto {
  x: number;
  y: number;
}

/** Foto tomada antes de abrir o cerrar, para animar hasta la posición nueva. */
const animacionPendiente = {
  foto: null as Map<string, Punto> | null,
  origenId: null as string | null,
  lanzada: false,
  esperando: false,
  reintentos: 0,
};

/**
 * Escucha el clic en la flecha antes de que PrimeNG mueva el árbol,
 * y guarda dónde está cada ficha.
 */
@Directive({
  selector: '[appOrganigramaAnimacion]',
  standalone: true,
})
export class OrganigramaAnimacionDirective implements OnInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly preparar = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const boton = target.closest('.p-organizationchart-node-toggle-button');
    if (!boton || !this.host.nativeElement.contains(boton)) {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const raiz = this.host.nativeElement as HTMLElement;
    const origen = boton
      .closest('.p-organizationchart-node')
      ?.querySelector('.organigrama-nodo') as HTMLElement | null;
    const foto = new Map<string, Punto>();
    for (const nodo of raiz.querySelectorAll('[data-org-id]')) {
      const pieza = nodo as HTMLElement;
      const id = pieza.dataset['orgId'];
      if (!id || !estaVisible(pieza)) {
        continue;
      }
      foto.set(id, centroDe(pieza));
    }
    animacionPendiente.foto = foto;
    animacionPendiente.origenId = origen?.dataset['orgId'] ?? null;
    animacionPendiente.lanzada = false;
    animacionPendiente.esperando = false;
    animacionPendiente.reintentos = 0;
    const piramide = boton.closest('.org-h');
    if (piramide instanceof HTMLElement) {
      ocultarToggles(piramide);
    }
  };

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.preparar, true);
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.preparar, true);
  }
}

function nodosGirados(raiz: HTMLElement): boolean {
  for (const nodo of raiz.querySelectorAll<HTMLElement>('.organigrama-nodo')) {
    if (nodo.offsetWidth >= 8 && nodo.dataset['girado'] !== '1') {
      return false;
    }
  }
  return true;
}

function intentarAnimacionOrganigrama(reintentar: () => void, listo: () => boolean): void {
  if (!animacionPendiente.foto || animacionPendiente.lanzada) {
    return;
  }
  if (!listo()) {
    if (animacionPendiente.esperando || animacionPendiente.reintentos >= 4) {
      return;
    }
    animacionPendiente.esperando = true;
    animacionPendiente.reintentos += 1;
    requestAnimationFrame(() => {
      animacionPendiente.esperando = false;
      reintentar();
    });
    return;
  }
  animacionPendiente.lanzada = true;
  const foto = animacionPendiente.foto;
  const origenId = animacionPendiente.origenId;
  animacionPendiente.foto = null;
  lanzarAnimacion(foto, origenId);
}

function lanzarAnimacion(foto: Map<string, Punto>, origenId: string | null): void {
  const origen = origenId ? foto.get(origenId) : undefined;
  const piezas = [...document.querySelectorAll<HTMLElement>('[data-org-id]')];
  for (const pieza of piezas) {
    pieza.style.transition = 'none';
    pieza.style.transform = pieza.classList.contains('organigrama-nodo') ? 'rotate(90deg)' : 'none';
  }
  document.body.getBoundingClientRect();

  const movimientos: { pieza: HTMLElement; desde: string; hasta: string }[] = [];
  for (const pieza of piezas) {
    const id = pieza.dataset['orgId'];
    if (!id) {
      continue;
    }
    if (!estaVisible(pieza)) {
      continue;
    }
    const ahora = centroDe(pieza);
    const antes = foto.get(id) ?? origen;
    if (!antes) {
      continue;
    }
    const screenDx = antes.x - ahora.x;
    const screenDy = antes.y - ahora.y;
    if (Math.abs(screenDx) < 0.5 && Math.abs(screenDy) < 0.5) {
      continue;
    }
    const enGiro = !!pieza.closest('.org-h__giro');
    // El gráfico está rotado -90°. Un traslado (dpx, dpy) en ese espacio
    // se ve en pantalla como (dpy, -dpx).
    const desde = enGiro
      ? `translate(${-screenDy}px, ${screenDx}px) rotate(90deg)`
      : `translate(${screenDx}px, ${screenDy}px)`;
    const hasta = enGiro ? 'rotate(90deg)' : 'none';
    movimientos.push({ pieza, desde, hasta });
  }

  for (const mov of movimientos) {
    mov.pieza.style.transform = mov.desde;
  }
  document.body.getBoundingClientRect();
  for (const mov of movimientos) {
    mov.pieza.style.transition = `transform ${DURACION_MS}ms ease-in-out`;
    mov.pieza.style.transform = mov.hasta;
    const alFin = (event: TransitionEvent): void => {
      if (event.propertyName !== 'transform' || event.target !== mov.pieza) {
        return;
      }
      mov.pieza.style.transition = '';
      mov.pieza.removeEventListener('transitionend', alFin);
    };
    mov.pieza.addEventListener('transitionend', alFin);
  }
}

function centroDe(elemento: HTMLElement): Punto {
  const rect = elemento.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function estaVisible(elemento: HTMLElement): boolean {
  const rect = elemento.getBoundingClientRect();
  return rect.width >= 8 && rect.height >= 8;
}

const APARECER_MS = 300;
const togglesTimer = new WeakMap<HTMLElement, number>();

/** La flecha se esconde al click y vuelve a verse cuando el árbol ya se acomodó. */
function ocultarToggles(piramide: HTMLElement): void {
  const previo = togglesTimer.get(piramide);
  if (previo) {
    window.clearTimeout(previo);
  }
  piramide.classList.add('org-toggles-ocultos');
  const timer = window.setTimeout(() => mostrarToggles(piramide), DURACION_MS);
  togglesTimer.set(piramide, timer);
}

function mostrarToggles(piramide: HTMLElement): void {
  const botones = [
    ...piramide.querySelectorAll('.p-organizationchart-node-toggle-button'),
  ] as HTMLElement[];
  for (const boton of botones) {
    boton.style.opacity = '0';
    boton.style.transition = 'none';
    boton.style.pointerEvents = 'none';
  }
  piramide.classList.remove('org-toggles-ocultos');
  piramide.getBoundingClientRect();
  for (const boton of botones) {
    boton.style.transition = `opacity ${APARECER_MS}ms ease`;
    boton.style.opacity = '1';
  }
  const timer = window.setTimeout(() => {
    for (const boton of botones) {
      boton.style.opacity = '';
      boton.style.transition = '';
      boton.style.pointerEvents = '';
    }
    togglesTimer.delete(piramide);
  }, APARECER_MS);
  togglesTimer.set(piramide, timer);
}
